import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { listImagesApiImagesGetOptions } from "../../generated/api/@tanstack/react-query.gen";
import type { HydratedImage } from "../../generated/api/types.gen";
import { useImageDownload } from "../../hooks/useImageDownload";
import { isOutOfIterations, useBillingStatus } from "../BillingStatusContext";
import { ErrorToast } from "../ErrorToast";
import { GalleryDetailsPanel } from "../GalleryDetailsPanel";
import { GalleryImageViewer } from "../GalleryImageViewer";
import { GalleryThumbnailCarousel } from "../GalleryThumbnailCarousel";
import { LoadingSpinner } from "../LoadingSpinner";
import { OutOfIterationsModal } from "../OutOfIterationsModal";

function computePreferredInitialImageIndex(
  sessionImages: HydratedImage[],
): number {
  if (sessionImages.length === 0) return 0;
  const refinedImages = sessionImages.filter(
    (img) => img.parameter_groups && img.parameter_groups.length > 0,
  );
  if (refinedImages.length > 0) {
    const mostRecentRefined = refinedImages[refinedImages.length - 1];
    const mostRecentRefinedIndex = sessionImages.findIndex(
      (img) => img.id === mostRecentRefined.id,
    );
    return mostRecentRefinedIndex !== -1
      ? mostRecentRefinedIndex
      : sessionImages.length - 1;
  }
  return sessionImages.length - 1;
}

function sortSessionImages(images: HydratedImage[]): HydratedImage[] {
  if (images.length === 0) return [];

  return [...images].sort((a, b) => {
    const aHasParams = a.parameter_groups && a.parameter_groups.length > 0;
    const bHasParams = b.parameter_groups && b.parameter_groups.length > 0;

    if (aHasParams !== bHasParams) {
      return aHasParams ? 1 : -1;
    }

    const aDate = new Date(a.generated_at).getTime();
    const bDate = new Date(b.generated_at).getTime();
    return aDate - bDate;
  });
}

function useImageContainerWidth(
  imageContainerRef: React.RefObject<HTMLDivElement | null>,
) {
  const [imageContainerWidth, setImageContainerWidth] = useState<
    number | undefined
  >();

  useEffect(() => {
    if (imageContainerRef.current) {
      const updateWidth = () => {
        setImageContainerWidth(imageContainerRef.current?.offsetWidth);
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(imageContainerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [imageContainerRef]);

  return imageContainerWidth;
}

function useThumbnailScroll(
  thumbnailContainerRef: React.RefObject<HTMLDivElement | null>,
  currentIndex: number,
  imageCount: number,
) {
  useEffect(() => {
    if (thumbnailContainerRef.current && imageCount > 0) {
      const thumbnails =
        thumbnailContainerRef.current.querySelectorAll("[data-thumbnail]");
      const activeThumbnail = thumbnails[currentIndex] as HTMLElement;
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [currentIndex, imageCount, thumbnailContainerRef]);
}

function useImageActions(
  currentImage: HydratedImage | null,
  currentIndex: number,
) {
  const navigate = useNavigate();
  const download = useImageDownload(currentImage, currentIndex + 1);
  const { billingStatus, isBillingLoading } = useBillingStatus();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const handleResumeFromHere = () => {
    if (!currentImage) return;
    // Browsing the gallery is free, but resuming means refining, which spends
    // an iteration. Interrupt here rather than at /refine so the user isn't
    // navigated away from what they were looking at.
    if (isOutOfIterations(billingStatus, isBillingLoading)) {
      setShowUpgradePrompt(true);
      return;
    }
    navigate({
      to: "/refine",
      search: {
        requestId: currentImage.request_id,
        imageId: currentImage.id,
        sessionId: currentImage.session_id,
      },
    });
  };

  return {
    handleResumeFromHere,
    showUpgradePrompt,
    dismissUpgradePrompt: () => setShowUpgradePrompt(false),
    ...download,
  };
}

function useImageNavigation(
  sessionImages: HydratedImage[],
  onExit: () => void,
) {
  const [currentIndex, setCurrentIndex] = useState(() =>
    computePreferredInitialImageIndex(sessionImages),
  );
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageContainerWidth = useImageContainerWidth(imageContainerRef);
  useThumbnailScroll(thumbnailContainerRef, currentIndex, sessionImages.length);

  const currentImage = sessionImages[currentIndex] ?? null;
  const hasMultipleIterations = sessionImages.length > 1;
  const actions = useImageActions(currentImage, currentIndex);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : sessionImages.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < sessionImages.length - 1 ? prev + 1 : 0));
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  useHotkeys("Escape", onExit, { enableOnFormTags: true });
  useHotkeys("ArrowLeft", () => hasMultipleIterations && handlePrevious(), {
    enableOnFormTags: true,
  });
  useHotkeys("ArrowRight", () => hasMultipleIterations && handleNext(), {
    enableOnFormTags: true,
  });

  return {
    currentIndex,
    currentImage,
    hasMultipleIterations,
    imageContainerRef,
    thumbnailContainerRef,
    imageContainerWidth,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    ...actions,
  };
}

function SessionMessageDisplay({
  title,
  message,
  onExit,
}: {
  title: string;
  message: string;
  onExit: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      <div className="text-xl text-[var(--text-primary)]">{title}</div>
      <p className="max-w-md text-[var(--text-secondary)]">{message}</p>
      <button
        type="button"
        onClick={onExit}
        className="text-[var(--accent-secondary-hover)] hover:text-[var(--accent-secondary-hover-stroke)]"
      >
        Back to gallery
      </button>
    </div>
  );
}

interface GallerySessionDetailInteractiveProps {
  sessionImages: HydratedImage[];
  onExit: () => void;
}

function GallerySessionDetailInteractive({
  sessionImages,
  onExit,
}: GallerySessionDetailInteractiveProps) {
  const {
    currentIndex,
    currentImage,
    hasMultipleIterations,
    imageContainerRef,
    thumbnailContainerRef,
    imageContainerWidth,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    handleResumeFromHere,
    showUpgradePrompt,
    dismissUpgradePrompt,
    handleDownload,
    isDownloading,
    downloadError,
    dismissDownloadError,
  } = useImageNavigation(sessionImages, onExit);

  if (!currentImage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16">
        <LoadingSpinner text="Loading session..." size="lg" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:flex-row">
      <ErrorToast message={downloadError} onDismiss={dismissDownloadError} />
      {showUpgradePrompt && (
        <OutOfIterationsModal onClose={dismissUpgradePrompt} />
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-2 lg:px-7 lg:pb-8 lg:pr-4 lg:pt-2">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden">
          <GalleryImageViewer
            ref={imageContainerRef}
            image={currentImage}
            currentIndex={currentIndex}
            totalCount={sessionImages.length}
            onResumeFromHere={handleResumeFromHere}
            onDownload={handleDownload}
            isDownloading={isDownloading}
          />
        </div>

        {hasMultipleIterations && (
          <GalleryThumbnailCarousel
            ref={thumbnailContainerRef}
            images={sessionImages}
            currentIndex={currentIndex}
            onSelect={handleThumbnailClick}
            onPrevious={handlePrevious}
            onNext={handleNext}
            imageContainerWidth={imageContainerWidth}
          />
        )}
      </div>

      <div className="scrollbar-hide flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-6 pb-8 pt-4 lg:w-[min(420px,38vw)] lg:px-5 lg:pb-8 lg:pr-7 lg:pt-4">
        <GalleryDetailsPanel
          variant="embedded"
          image={currentImage}
          currentIndex={currentIndex}
          totalCount={sessionImages.length}
          onPrevious={handlePrevious}
          onNext={handleNext}
          hasMultipleIterations={hasMultipleIterations}
        />
      </div>
    </div>
  );
}

interface GallerySessionDetailViewProps {
  sessionId: string;
  onExit: () => void;
}

export function GallerySessionDetailView({
  sessionId,
  onExit,
}: GallerySessionDetailViewProps) {
  const {
    data: sessionData,
    isLoading,
    error,
  } = useQuery({
    ...listImagesApiImagesGetOptions({
      query: { session_id: sessionId },
    }),
    enabled: Boolean(sessionId),
  });

  const sessionImages = useMemo(
    () => sortSessionImages((sessionData?.images ?? []) as HydratedImage[]),
    [sessionData?.images],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16">
        <LoadingSpinner text="Loading session..." size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <SessionMessageDisplay
        title="Error loading session"
        message="The session could not be loaded. It may have been deleted or you may not have access."
        onExit={onExit}
      />
    );
  }

  if (sessionData && sessionImages.length === 0) {
    return (
      <SessionMessageDisplay
        title="Session is empty"
        message="This session exists but contains no images."
        onExit={onExit}
      />
    );
  }

  return (
    <GallerySessionDetailInteractive
      key={sessionId}
      sessionImages={sessionImages}
      onExit={onExit}
    />
  );
}
